import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Topbar } from "../components/shell/Topbar";
import { useLayoutContext } from "../layouts/DashboardLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Skeleton } from "../components/ui/skeleton";
import { cn } from "@/packages/utils/cn";

type Tab = "general" | "audiences" | "schedule";

interface Connection {
  id: string;
  repoName: string;
  repoUrl: string;
}
interface Audience {
  id: string;
  name: string;
  description: string | null;
  color: string;
  enabledByDefault: boolean;
  emailEnabled: boolean;
  emailRecipients: string[];
  slackEnabled: boolean;
  slackChannelId: string | null;
  slackChannelName: string | null;
}

interface Settings {
  emailSettings: {
    primaryColor: string;
    secondaryColor: string;
    backgroundColor: string;
    font: string;
    footerText: string | null;
    logoPreviewUrl?: string | null;
  } | null;
  slackIntegration: {
    botToken: string;
    workspaceName?: string | null;
  } | null;
  projectConfig: {
    publishMode: string;
    scheduleEnabled: boolean;
    scheduleFrequency: string | null;
    scheduleEveryN: number | null;
    scheduleUnit: string | null;
  } | null;
}

const PALETTE = ["#C8362C", "#3E7C5C", "#C7903A", "#8C7AB0", "#1B1F2A"];

export function ReleaseSettingsPage() {
  const { teamId, projectId } = useParams();
  const { teams, projects } = useLayoutContext();
  const team = teams.find((t) => t.id === teamId);
  const project = (projects[teamId ?? ""] ?? []).find((p) => p.referenceId === projectId);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get("tab") as Tab) ?? "general";
  const [tab, setTab] = useState<Tab>(initialTab);

  const setActiveTab = (t: Tab) => {
    setTab(t);
    searchParams.set("tab", t);
    setSearchParams(searchParams, { replace: true });
  };

  if (!team || !project) return null;

  const tabs: Array<{ id: Tab; label: string }> = [
    { id: "general", label: "General" },
    { id: "audiences", label: "Audiences" },
    { id: "schedule", label: "Schedule" },
  ];

  return (
    <>
      <Topbar
        title="Release settings"
        crumbs={[
          { label: team.name, to: `/teams/${team.id}` },
          { label: project.name, to: `/teams/${team.id}/projects/${project.referenceId}` },
          {
            label: "Releases",
            to: `/teams/${team.id}/projects/${project.referenceId}/releases`,
          },
          { label: "Settings" },
        ]}
      />
      <div className="border-b border-rule px-4 sm:px-8 flex gap-1 bg-paper">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={cn(
              "px-3.5 py-3 text-sm border-b-2 transition-colors",
              tab === t.id
                ? "border-foreground text-foreground font-semibold"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-auto px-4 sm:px-8 py-6">
        {tab === "general" && <GeneralTab projectId={projectId!} />}
        {tab === "audiences" && <AudiencesTab projectId={projectId!} />}
        {tab === "schedule" && <ScheduleTab projectId={projectId!} />}
      </div>
    </>
  );
}

// ─── General ───────────────────────────────────────────────────────────

function GeneralTab({ projectId }: { projectId: string }) {
  const { teams } = useLayoutContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [connections, setConnections] = useState<Connection[]>([]);
  const [githubConnected, setGithubConnected] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const refresh = () => {
    fetch(`/api/projects/${projectId}/github-connections`)
      .then((r) => r.json())
      .then(setConnections);
    fetch(`/api/projects/${projectId}/github/status`)
      .then((r) => r.json())
      .then((d) => setGithubConnected(d.connected));
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    if (searchParams.get("connected") === "true") {
      setPickerOpen(true);
      searchParams.delete("connected");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removeConnection = async (id: string) => {
    if (!confirm("Remove this repo?")) return;
    await fetch(`/api/projects/${projectId}/github-connections/${id}`, {
      method: "DELETE",
    });
    refresh();
  };

  const disconnectGithub = async () => {
    if (
      !confirm(
        "Disconnect GitHub? Connected repos will stop syncing commits until you reconnect.",
      )
    )
      return;
    await fetch(`/api/projects/${projectId}/github/disconnect`, {
      method: "DELETE",
    });
    refresh();
  };

  const teamId = teams[0]?.id ?? "";

  return (
    <div className="max-w-2xl space-y-6">
      <section>
        <h2 className="font-serif text-xl font-semibold tracking-tight">
          GitHub repositories
        </h2>
        <p className="text-sm text-muted-foreground mt-1 mb-4">
          Connect repos so we can fetch commits and draft release notes.
        </p>

        {!githubConnected ? (
          <a
            href={`/api/projects/${projectId}/github/connect?teamId=${teamId}`}
          >
            <Button>Connect GitHub</Button>
          </a>
        ) : (
          <div className="space-y-2">
            {connections.length === 0 ? (
              <div className="text-sm text-muted-foreground italic">
                Connected to GitHub. Pick a repo to add.
              </div>
            ) : (
              connections.map((c) => (
                <div
                  key={c.id}
                  className="flex items-center justify-between border border-rule rounded-md px-3 py-2 bg-card-paper"
                >
                  <span className="font-mono text-sm">{c.repoName}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeConnection(c.id)}
                  >
                    Remove
                  </Button>
                </div>
              ))
            )}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPickerOpen(true)}
              >
                + Add repo via GitHub
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={disconnectGithub}
              >
                Disconnect GitHub
              </Button>
            </div>
          </div>
        )}
      </section>

      <EmailBrandingSection projectId={projectId} />

      {pickerOpen && (
        <GitHubRepoPickerDialog
          projectId={projectId}
          connectedRepoNames={connections.map((c) => c.repoName)}
          onClose={() => setPickerOpen(false)}
          onSaved={() => {
            setPickerOpen(false);
            refresh();
          }}
        />
      )}
    </div>
  );
}

// ─── GitHub repo picker ────────────────────────────────────────────────

interface GithubOrg {
  login: string;
  avatarUrl: string;
  isPersonal: boolean;
}
interface GithubRepo {
  id: number;
  name: string;
  fullName: string;
  description: string | null;
  private: boolean;
  htmlUrl: string;
}

function GitHubRepoPickerDialog({
  projectId,
  connectedRepoNames,
  onClose,
  onSaved,
}: {
  projectId: string;
  connectedRepoNames: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const connectedSet = new Set(connectedRepoNames);
  const [orgs, setOrgs] = useState<GithubOrg[] | null>(null);
  const [selectedOrg, setSelectedOrg] = useState<GithubOrg | null>(null);
  const [repos, setRepos] = useState<GithubRepo[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [reposLoading, setReposLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch(`/api/projects/${projectId}/github/orgs`)
      .then(async (r) => {
        if (!r.ok)
          throw new Error(
            (await r.json().catch(() => ({}))).error ??
              "Failed to load GitHub accounts",
          );
        return r.json() as Promise<GithubOrg[]>;
      })
      .then(setOrgs)
      .catch((err) => setError(err.message));
  }, [projectId]);

  const loadRepos = async (org: GithubOrg, nextPage: number) => {
    setReposLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/projects/${projectId}/github/repos?org=${encodeURIComponent(
          org.login,
        )}&personal=${org.isPersonal}&page=${nextPage}`,
      );
      if (!res.ok)
        throw new Error(
          (await res.json().catch(() => ({}))).error ??
            "Failed to load repositories",
        );
      const data = (await res.json()) as GithubRepo[];
      setRepos((prev) => (nextPage === 1 ? data : [...prev, ...data]));
      setHasMore(data.length === 50);
      setPage(nextPage);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load repositories");
    } finally {
      setReposLoading(false);
    }
  };

  const selectOrg = (org: GithubOrg) => {
    setSelectedOrg(org);
    setRepos([]);
    loadRepos(org, 1);
  };

  const connectRepo = async (repo: GithubRepo) => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/projects/${projectId}/github/connect-repo`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            repoFullName: repo.fullName,
            repoUrl: repo.htmlUrl,
          }),
        },
      );
      if (!res.ok)
        throw new Error(
          (await res.json().catch(() => ({}))).error ?? "Failed to add repo",
        );
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add repo");
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {selectedOrg ? `Repos in ${selectedOrg.login}` : "Pick an account"}
          </DialogTitle>
          <DialogDescription>
            {selectedOrg
              ? "Select a repository to connect to this project."
              : "Choose the account or organization that owns the repo."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 max-h-[60vh] overflow-auto">
          {error && (
            <div className="text-sm text-signal-red px-1">{error}</div>
          )}

          {!selectedOrg ? (
            !orgs ? (
              <Skeleton className="h-32 w-full" />
            ) : (
              orgs.map((org) => (
                <button
                  key={org.login}
                  onClick={() => selectOrg(org)}
                  className="w-full flex items-center gap-3 border border-rule rounded-md px-3 py-2 bg-card-paper hover:bg-panel/40 text-left"
                >
                  <img
                    src={org.avatarUrl}
                    alt=""
                    className="size-6 rounded-full"
                  />
                  <span className="text-sm font-medium">{org.login}</span>
                  {org.isPersonal && (
                    <span className="font-mono text-[10px] px-2 py-0.5 rounded-full bg-panel border border-rule-soft">
                      personal
                    </span>
                  )}
                </button>
              ))
            )
          ) : (
            <>
              {reposLoading && repos.length === 0 ? (
                <Skeleton className="h-32 w-full" />
              ) : repos.length === 0 ? (
                <div className="text-sm text-muted-foreground italic px-1">
                  No repositories found.
                </div>
              ) : (
                repos.map((repo) => {
                  const alreadyConnected = connectedSet.has(repo.fullName);
                  return (
                    <button
                      key={repo.id}
                      onClick={() => connectRepo(repo)}
                      disabled={submitting || alreadyConnected}
                      className="w-full flex items-start justify-between gap-3 border border-rule rounded-md px-3 py-2 bg-card-paper hover:bg-panel/40 text-left disabled:opacity-50 disabled:hover:bg-card-paper disabled:cursor-not-allowed"
                    >
                      <span className="min-w-0">
                        <span className="font-mono text-sm block truncate">
                          {repo.name}
                        </span>
                        {repo.description && (
                          <span className="text-xs text-muted-foreground block truncate">
                            {repo.description}
                          </span>
                        )}
                      </span>
                      <span className="flex items-center gap-1.5 shrink-0">
                        {repo.private && (
                          <span className="font-mono text-[10px] px-2 py-0.5 rounded-full bg-panel border border-rule-soft">
                            private
                          </span>
                        )}
                        {alreadyConnected && (
                          <span className="font-mono text-[10px] px-2 py-0.5 rounded-full bg-panel border border-rule-soft">
                            added
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })
              )}
              {hasMore && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  disabled={reposLoading}
                  onClick={() => loadRepos(selectedOrg, page + 1)}
                >
                  {reposLoading ? "Loading…" : "Load more"}
                </Button>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          {selectedOrg && (
            <Button
              variant="ghost"
              onClick={() => {
                setSelectedOrg(null);
                setRepos([]);
                setError(null);
              }}
              disabled={submitting}
            >
              Back
            </Button>
          )}
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Email branding (lives inside General tab) ─────────────────────────

interface EmailBranding {
  primaryColor: string;
  secondaryColor: string;
  backgroundColor: string;
  font: string;
  footerText: string | null;
  logoPreviewUrl?: string | null;
}

const DEFAULT_BRANDING: EmailBranding = {
  primaryColor: "#000000",
  secondaryColor: "#666666",
  backgroundColor: "#f9f9f9",
  font: "system-ui",
  footerText: null,
  logoPreviewUrl: null,
};

function EmailBrandingSection({ projectId }: { projectId: string }) {
  const [draft, setDraft] = useState<EmailBranding | null>(null);
  const [saving, setSaving] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);

  const refresh = () => {
    fetch(`/api/projects/${projectId}/release-settings`)
      .then((r) => r.json())
      .then((d: Settings) => {
        setDraft(d.emailSettings ?? DEFAULT_BRANDING);
      });
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  if (!draft) {
    return (
      <section className="border-t border-rule pt-6">
        <Skeleton className="h-6 w-40 mb-3" />
        <Skeleton className="h-32 w-full" />
      </section>
    );
  }

  const update = (patch: Partial<EmailBranding>) =>
    setDraft({ ...draft, ...patch });

  const save = async () => {
    setSaving(true);
    await fetch(`/api/projects/${projectId}/release-settings`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        emailSettings: {
          primaryColor: draft.primaryColor,
          secondaryColor: draft.secondaryColor,
          backgroundColor: draft.backgroundColor,
          font: draft.font,
          footerText: draft.footerText,
        },
      }),
    });
    setSaving(false);
    refresh();
  };

  const uploadLogo = async (file: File) => {
    setLogoBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(
      `/api/projects/${projectId}/release-settings/email/logo`,
      { method: "POST", body: fd },
    );
    if (res.ok) {
      const data = (await res.json()) as { emailSettings: EmailBranding };
      setDraft({ ...draft, logoPreviewUrl: data.emailSettings.logoPreviewUrl });
    }
    setLogoBusy(false);
  };

  const removeLogo = async () => {
    if (!confirm("Remove the email logo?")) return;
    setLogoBusy(true);
    await fetch(`/api/projects/${projectId}/release-settings/email/logo`, {
      method: "DELETE",
    });
    setDraft({ ...draft, logoPreviewUrl: null });
    setLogoBusy(false);
  };

  return (
    <section className="border-t border-rule pt-6">
      <h2 className="font-serif text-xl font-semibold tracking-tight">
        Email branding
      </h2>
      <p className="text-sm text-muted-foreground mt-1 mb-4">
        How your release emails look. Background, colors, logo and footer apply
        to every audience that receives email.
      </p>

      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label>Logo</Label>
          {draft.logoPreviewUrl ? (
            <div className="flex items-center gap-3 border border-rule rounded-md p-3 bg-card-paper">
              <img
                src={draft.logoPreviewUrl}
                alt="Email logo"
                className="max-h-12"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={removeLogo}
                disabled={logoBusy}
              >
                Remove
              </Button>
            </div>
          ) : (
            <label className="inline-block">
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadLogo(f);
                }}
              />
              <span className="inline-flex items-center px-3 py-1.5 border border-rule rounded-md bg-card-paper text-sm cursor-pointer hover:bg-panel/40">
                {logoBusy ? "Uploading…" : "Upload logo"}
              </span>
            </label>
          )}
        </div>

        <div className="grid grid-cols-3 gap-3">
          <ColorField
            label="Background"
            value={draft.backgroundColor}
            onChange={(v) => update({ backgroundColor: v })}
          />
          <ColorField
            label="Text (primary)"
            value={draft.primaryColor}
            onChange={(v) => update({ primaryColor: v })}
          />
          <ColorField
            label="Accent"
            value={draft.secondaryColor}
            onChange={(v) => update({ secondaryColor: v })}
          />
        </div>

        <div className="space-y-1.5">
          <Label>Font family</Label>
          <Input
            value={draft.font}
            onChange={(e) => update({ font: e.target.value })}
            placeholder="system-ui"
          />
          <p className="text-xs text-muted-foreground">
            CSS font stack (e.g. <code className="font-mono">Inter, system-ui</code>).
          </p>
        </div>

        <div className="space-y-1.5">
          <Label>Footer text</Label>
          <Textarea
            value={draft.footerText ?? ""}
            onChange={(e) =>
              update({ footerText: e.target.value || null })
            }
            rows={3}
            placeholder="© 2026 Acme · Unsubscribe link, contact email, etc."
          />
        </div>

        <div
          className="border border-rule rounded-md p-4 text-sm"
          style={{
            background: draft.backgroundColor,
            color: draft.primaryColor,
            fontFamily: draft.font,
          }}
        >
          <div className="font-semibold text-base mb-1">Preview</div>
          <div
            style={{
              borderTop: `2px solid ${draft.secondaryColor}`,
              margin: "6px 0 8px",
            }}
          />
          <div>
            This is how the body of your release emails will look. All the
            text uses the primary color over the background color.
          </div>
        </div>

        <Button onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save email branding"}
        </Button>
      </div>
    </section>
  );
}

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div className="flex items-center gap-2 border border-rule rounded-md px-2 py-1 bg-card-paper">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="size-7 cursor-pointer rounded border-0 bg-transparent p-0"
        />
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 bg-transparent text-sm font-mono outline-none"
        />
      </div>
    </div>
  );
}

// ─── Audiences ─────────────────────────────────────────────────────────

function AudiencesTab({ projectId }: { projectId: string }) {
  const [audiences, setAudiences] = useState<Audience[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Audience | null>(null);
  const [creating, setCreating] = useState(false);

  const refresh = () => {
    setLoading(true);
    fetch(`/api/projects/${projectId}/audiences`)
      .then((r) => r.json())
      .then(setAudiences)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const remove = async (id: string) => {
    if (!confirm("Delete this audience?")) return;
    await fetch(`/api/projects/${projectId}/audiences/${id}`, {
      method: "DELETE",
    });
    refresh();
  };

  return (
    <div className="max-w-3xl">
      <div className="text-eyebrow mb-2">02 · Audiences</div>
      <h2 className="font-serif text-2xl font-semibold tracking-tight">
        Who hears about your releases
      </h2>
      <p className="font-serif italic text-sm text-muted-foreground mb-6">
        Audiences are reusable groups. Each audience has its own transport
        config (email, slack), and releases pick which audiences to dispatch
        to.
      </p>

      <div className="bg-card-paper border border-rule rounded-lg overflow-hidden">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="grid grid-cols-[auto_1fr_auto] gap-4 px-5 py-4 border-t border-rule-soft first:border-t-0 items-center"
            >
              <Skeleton className="size-3 rounded-sm" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-3 w-64" />
              </div>
              <Skeleton className="h-8 w-16" />
            </div>
          ))
        ) : audiences.length === 0 ? (
          <div className="px-5 py-6 text-sm text-muted-foreground text-center">
            No audiences yet.
          </div>
        ) : (
          audiences.map((a) => (
            <div
              key={a.id}
              className="grid grid-cols-[auto_1fr_auto] gap-4 px-5 py-4 border-t border-rule-soft first:border-t-0 items-center"
            >
              <span
                className="size-3 rounded-sm"
                style={{ background: a.color }}
              />
              <div>
                <div className="font-semibold text-sm">{a.name}</div>
                {a.description && (
                  <div className="text-xs text-muted-foreground">
                    {a.description}
                  </div>
                )}
                <div className="flex gap-1.5 mt-1.5 flex-wrap">
                  {a.emailEnabled && (
                    <span className="font-mono text-[10px] px-2 py-0.5 rounded-full bg-panel border border-rule-soft">
                      email · {a.emailRecipients.length}
                    </span>
                  )}
                  {a.slackEnabled && a.slackChannelName && (
                    <span className="font-mono text-[10px] px-2 py-0.5 rounded-full bg-panel border border-rule-soft">
                      #{a.slackChannelName}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditing(a)}
                >
                  Edit
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => remove(a.id)}
                >
                  Delete
                </Button>
              </div>
            </div>
          ))
        )}
        <div
          className="px-5 py-3 border-t border-rule bg-panel/40 font-mono text-xs text-signal-red cursor-pointer text-center"
          onClick={() => setCreating(true)}
        >
          + Add audience
        </div>
      </div>

      {(editing || creating) && (
        <AudienceDialog
          projectId={projectId}
          audience={editing}
          onClose={() => {
            setEditing(null);
            setCreating(false);
          }}
          onSaved={() => {
            refresh();
            setEditing(null);
            setCreating(false);
          }}
        />
      )}
    </div>
  );
}

function AudienceDialog({
  projectId,
  audience,
  onClose,
  onSaved,
}: {
  projectId: string;
  audience: Audience | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<Audience>(
    audience ?? {
      id: "",
      name: "",
      description: "",
      color: PALETTE[0]!,
      enabledByDefault: true,
      emailEnabled: false,
      emailRecipients: [],
      slackEnabled: false,
      slackChannelId: null,
      slackChannelName: null,
    },
  );
  const [recipientInput, setRecipientInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const url = audience
        ? `/api/projects/${projectId}/audiences/${audience.id}`
        : `/api/projects/${projectId}/audiences`;
      const method = audience ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        throw new Error(
          (await res.json().catch(() => ({}))).error ?? "Failed to save audience",
        );
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save audience");
    } finally {
      setSubmitting(false);
    }
  };

  const addRecipient = () => {
    const e = recipientInput.trim();
    if (!e) return;
    setForm({ ...form, emailRecipients: [...form.emailRecipients, e] });
    setRecipientInput("");
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{audience ? "Edit audience" : "New audience"}</DialogTitle>
          <DialogDescription>
            One audience per recipient group. Configure transports below.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 max-h-[60vh] overflow-auto">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea
              value={form.description ?? ""}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Color</Label>
            <div className="flex gap-2">
              {PALETTE.map((c) => (
                <button
                  key={c}
                  onClick={() => setForm({ ...form, color: c })}
                  className={cn(
                    "size-7 rounded-md border-2",
                    form.color === c ? "border-foreground" : "border-transparent",
                  )}
                  style={{ background: c }}
                />
              ))}
            </div>
          </div>

          <div className="border-t border-rule pt-3">
            <div className="flex items-center justify-between mb-2">
              <Label>Email</Label>
              <Switch
                checked={form.emailEnabled}
                onCheckedChange={(v) => setForm({ ...form, emailEnabled: v })}
              />
            </div>
            {form.emailEnabled && (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input
                    value={recipientInput}
                    onChange={(e) => setRecipientInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addRecipient();
                      }
                    }}
                    placeholder="someone@team.com"
                  />
                  <Button variant="outline" onClick={addRecipient}>
                    Add
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {form.emailRecipients.map((r) => (
                    <span
                      key={r}
                      className="px-2 py-0.5 bg-panel border border-rule-soft rounded-full text-xs font-mono inline-flex items-center gap-1"
                    >
                      {r}
                      <button
                        onClick={() =>
                          setForm({
                            ...form,
                            emailRecipients: form.emailRecipients.filter(
                              (x) => x !== r,
                            ),
                          })
                        }
                        className="text-faint hover:text-signal-red"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-rule pt-3">
            <div className="flex items-center justify-between mb-2">
              <Label>Slack</Label>
              <Switch
                checked={form.slackEnabled}
                onCheckedChange={(v) => setForm({ ...form, slackEnabled: v })}
              />
            </div>
            {form.slackEnabled && (
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">Channel ID</Label>
                  <Input
                    value={form.slackChannelId ?? ""}
                    onChange={(e) =>
                      setForm({ ...form, slackChannelId: e.target.value })
                    }
                    placeholder="C0XXXXXXXX"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Channel name</Label>
                  <Input
                    value={form.slackChannelName ?? ""}
                    onChange={(e) =>
                      setForm({ ...form, slackChannelName: e.target.value })
                    }
                    placeholder="launches"
                  />
                </div>
                <div className="col-span-2 text-xs text-muted-foreground">
                  Slack workspace bot token is configured at the project level.
                  Set it in <b>General</b> if you haven't.
                </div>
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="text-sm text-signal-red px-1">{error}</div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={submitting || !form.name}>
            {audience
              ? submitting
                ? "Saving…"
                : "Save changes"
              : submitting
                ? "Creating…"
                : "Create audience"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Schedule ──────────────────────────────────────────────────────────

function ScheduleTab({ projectId }: { projectId: string }) {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`/api/projects/${projectId}/release-settings`)
      .then((r) => r.json())
      .then(setSettings);
  }, [projectId]);

  if (!settings) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const cfg = settings.projectConfig ?? {
    publishMode: "manual",
    scheduleEnabled: false,
    scheduleFrequency: null,
    scheduleEveryN: null,
    scheduleUnit: null,
  };

  const save = async () => {
    setSaving(true);
    await fetch(`/api/projects/${projectId}/release-settings`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ projectConfig: cfg }),
    });
    setSaving(false);
  };

  const update = (patch: Partial<NonNullable<Settings["projectConfig"]>>) => {
    setSettings({
      ...settings,
      projectConfig: { ...cfg, ...patch },
    });
  };

  return (
    <div className="max-w-2xl space-y-6">
      <section>
        <h2 className="font-serif text-xl font-semibold tracking-tight">
          Publish mode
        </h2>
        <p className="text-sm text-muted-foreground mb-3">
          Manual = drafts wait for review. Auto = generated releases publish
          immediately.
        </p>
        <div className="flex gap-2">
          {(["manual", "auto"] as const).map((m) => (
            <button
              key={m}
              onClick={() => update({ publishMode: m })}
              className={cn(
                "px-3 py-1.5 rounded-md border text-sm",
                cfg.publishMode === m
                  ? "border-foreground bg-ink text-paper font-semibold"
                  : "border-rule bg-card-paper",
              )}
            >
              {m}
            </button>
          ))}
        </div>
      </section>

      <section className="border-t border-rule pt-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-serif text-xl font-semibold tracking-tight">
            Automatic release creation
          </h2>
          <Switch
            checked={cfg.scheduleEnabled}
            onCheckedChange={(v) => update({ scheduleEnabled: v })}
          />
        </div>
        <p className="text-sm text-muted-foreground mb-3">
          Periodically generate a new release from your connected repos.
        </p>
        {cfg.scheduleEnabled && (
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>Frequency</Label>
              <select
                className="w-full border border-rule rounded-md px-2 py-1.5 bg-card-paper"
                value={cfg.scheduleFrequency ?? "weekly"}
                onChange={(e) =>
                  update({ scheduleFrequency: e.target.value })
                }
              >
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            {cfg.scheduleFrequency === "custom" && (
              <>
                <div className="space-y-1.5">
                  <Label>Every</Label>
                  <Input
                    type="number"
                    value={cfg.scheduleEveryN ?? 1}
                    onChange={(e) =>
                      update({ scheduleEveryN: Number(e.target.value) })
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Unit</Label>
                  <select
                    className="w-full border border-rule rounded-md px-2 py-1.5 bg-card-paper"
                    value={cfg.scheduleUnit ?? "weeks"}
                    onChange={(e) => update({ scheduleUnit: e.target.value })}
                  >
                    <option value="weeks">weeks</option>
                    <option value="months">months</option>
                  </select>
                </div>
              </>
            )}
          </div>
        )}
      </section>

      <Button onClick={save} disabled={saving}>
        {saving ? "Saving…" : "Save changes"}
      </Button>
    </div>
  );
}
